import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertsPanel } from '../components/dashboard/AlertsPanel';
import { KPITiles } from '../components/dashboard/KPITiles';
import { LiveFeed } from '../components/dashboard/LiveFeed';
import { OperationalHealth } from '../components/dashboard/OperationalHealth';
import { TopRestaurant } from '../components/dashboard/TopRestaurant';
import { TopCustomersList } from '../components/dashboard/TopCustomersList';
import { TopRestaurantsList } from '../components/dashboard/TopRestaurantsList';
import { Button } from '../components/ui/Button';
import { AnalyticsIcon, LiveOrdersIcon, OrdersIcon, PlusIcon } from '../components/ui/Icons';
import { useDashboard } from '../hooks/useDashboard';
import { usePendingOrdersCount } from '../hooks/usePendingOrdersCount';

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { metrics, loading, error, fetchMetrics } = useDashboard();
  const { count: pendingOrdersCount } = usePendingOrdersCount();

  useEffect(() => {
    // Initial fetch on mount (no auto-refresh)
    fetchMetrics();
  }, [fetchMetrics]);

  if (error) {
    return (
      <div className="min-h-screen">
        <div className="bg-white/80 backdrop-blur-xl px-8 py-6">
          <h1 className="text-3xl font-semibold text-gray-900">Dashboard</h1>
        </div>
        <div className="p-8">
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-8">
            <div className="text-center py-12">
              <p className="text-red-600 text-lg mb-4">Failed to load dashboard: {error}</p>
              <Button onClick={() => window.location.reload()}>Retry</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header Section - Same color as sidebar */}
      <div className="bg-white/80 backdrop-blur-xl px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900 mb-1">Dashboard</h1>
            <p className="text-sm text-gray-500">👋🏾 Hi there, welcome back!</p>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={fetchMetrics} disabled={loading}>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Quick Actions Bar - Prominent and accessible */}
      <div className="px-8 pt-6 pb-2">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <span className="text-xl">⚡</span>
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Primary Action - Live Orders (Most Critical) */}
          <button
            onClick={() => navigate('/live-orders')}
            className="group relative bg-admin-orange-500 hover:bg-admin-orange-600 rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-200 border-2 border-admin-orange-600 hover:border-admin-orange-700 active:scale-[0.98]"
          >
            <div className="relative flex items-center gap-3">
              <div className="w-11 h-11 bg-white/25 rounded-lg flex items-center justify-center flex-shrink-0 relative">
                <LiveOrdersIcon size="md" className="text-white" />
                {pendingOrdersCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-md ring-2 ring-white animate-pulse">
                    {pendingOrdersCount}
                  </span>
                )}
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-semibold text-white text-sm mb-0.5">Live Orders</h3>
                <p className="text-xs text-white/90 font-medium">
                  {pendingOrdersCount > 0 ? `${pendingOrdersCount} pending` : 'Monitor orders'}
                </p>
              </div>
              <svg
                className="w-5 h-5 text-white/60 group-hover:text-white/80 transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </button>

          {/* Secondary Actions */}
          <button
            onClick={() => navigate('/restaurants')}
            className="group bg-white hover:bg-gray-50 border-2 border-gray-300 hover:border-gray-400 rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.98]"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-gray-100 group-hover:bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-200">
                <PlusIcon size="md" className="text-gray-700" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-semibold text-gray-900 text-sm mb-0.5">Add Restaurant</h3>
                <p className="text-xs text-gray-600">Create new listing</p>
              </div>
              <svg
                className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </button>

          <button
            onClick={() => navigate('/orders')}
            className="group bg-white hover:bg-gray-50 border-2 border-gray-300 hover:border-gray-400 rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.98]"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-gray-100 group-hover:bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-200">
                <OrdersIcon size="md" className="text-gray-700" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-semibold text-gray-900 text-sm mb-0.5">Order History</h3>
                <p className="text-xs text-gray-600">View all orders</p>
              </div>
              <svg
                className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </button>

          <button
            onClick={() => navigate('/analytics')}
            className="group bg-white hover:bg-gray-50 border-2 border-gray-300 hover:border-gray-400 rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.98]"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-gray-100 group-hover:bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-200">
                <AnalyticsIcon size="md" className="text-gray-700" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-semibold text-gray-900 text-sm mb-0.5">Analytics</h3>
                <p className="text-xs text-gray-600">View insights</p>
              </div>
              <svg
                className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <span className="text-xl">📊</span>
          Today's Overview
        </h2>

        {/* Main Content - Full Width */}
        <div className="space-y-6">
          {/* KPI Tiles */}
          <div>
            <KPITiles
              kpis={
                metrics?.kpis || {
                  total_orders_today: 0,
                  delivered_orders_today: 0,
                  cancelled_orders_today: 0,
                  cancellation_rate: 0,
                  gross_revenue_today: 0,
                  net_revenue_today: 0,
                  avg_order_value: 0,
                  completion_rate: 0,
                  active_restaurants_today: 0,
                  active_users_today: 0,
                  delta_orders: 0,
                  delta_revenue: 0,
                  delivery_fee_revenue_today: 0,
                }
              }
              loading={loading}
            />
          </div>

          {/* Operational Health & Top Restaurant */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <OperationalHealth
              health={
                metrics?.operational_health || {
                  avg_time_placed_to_delivered: 0,
                  p90_placed_to_delivered: 0,
                  late_orders_count: 0,
                  failed_payments_count: 0,
                }
              }
              loading={loading}
            />
            <TopRestaurant restaurant={metrics?.top_restaurant || null} loading={loading} />
          </div>

          {/* Top Restaurants & Customers */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <TopRestaurantsList restaurants={metrics?.top_restaurants || []} loading={loading} />
            <TopCustomersList customers={metrics?.top_customers || []} loading={loading} />
          </div>

          {/* Live Feed & Alerts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <LiveFeed feed={metrics?.live_feed || []} loading={loading} />
            <AlertsPanel alerts={metrics?.alerts || []} loading={loading} />
          </div>
        </div>
      </div>
    </div>
  );
};
